import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    FaPhoneAlt,
    FaEnvelope,
    FaMapMarkerAlt,
    FaChevronRight,
    FaPaperPlane,
    FaFacebookF,
    FaInstagram,
    FaYoutube,
    FaLock,
    FaHeart
} from 'react-icons/fa';
import '../css/footer.css';
import Logo from '../assets/ngoLogo.webp';
import * as contentService from '../services/contentService';

const DEFAULT_SETTINGS = {
    siteName: 'Loving Arms',
    tagline: 'Orphanage',
    footerDescription: 'Dedicated to uplifting street children and vulnerable youth through shelter, education, and compassionate care. Regd. under Indian Trust Act 1882 (#115/IV 2009).',
    phone: '+91-9603040740',
    email: 'lovingarmsorphanage@gmail.com',
    address: 'Rajampeta Town, YSR Kadapa, AP, India',
    socialLinks: {
        facebook: 'https://www.facebook.com/Vekatasrikanth',
        instagram: 'https://www.instagram.com/lovingarms_orphanage/?hl=en',
        youtube: 'https://www.youtube.com'
    }
};

export default function Footer() {
    const [email, setEmail] = useState('');
    const [subscribed, setSubscribed] = useState(false);
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);

    useEffect(() => {
        const fetchFooterData = async () => {
            try {
                const s = await contentService.getSiteSettings();
                if (s) {
                    setSettings(prev => ({
                        ...prev,
                        ...s,
                        socialLinks: { ...prev.socialLinks, ...(s.socialLinks || {}) }
                    }));
                }
            } catch (err) {
                console.error("Failed to load footer settings:", err);
            }
        };

        fetchFooterData();
    }, []);

    function onsubmitBtn(e) {
        e.preventDefault();
        if (email.trim()) {
            setSubscribed(true);
            setEmail('');
            setTimeout(() => setSubscribed(false), 5000);
        }
    }

    return (
        <footer className="modern-footer">
            <div className="footer-top-accent"></div>
            <div className="container footer-content">
                <div className="row g-4">
                    {/* Brand and Mission */}
                    <div className="col-lg-4 col-md-6 footer-brand">
                        <Link to="/" className="footer-logo-row">
                            <img src={Logo} alt="Loving Arms Logo" className="footer-logo-img" />
                            <div>
                                <span className="footer-brand-title">{settings.siteName || "Loving Arms"}</span>
                                <span className="footer-brand-sub">{settings.tagline || "Orphanage"}</span>
                            </div>
                        </Link>
                        <p className="footer-desc">
                            {settings.footerDescription}
                        </p>
                        <div className="footer-contact-items">
                            <a href={`tel:${settings.phone}`} className="footer-contact-link">
                                <FaPhoneAlt />
                                <span>{settings.phone}</span>
                            </a>
                            <a href={`mailto:${settings.email}`} className="footer-contact-link">
                                <FaEnvelope />
                                <span>{settings.email}</span>
                            </a>
                            <span className="footer-contact-link">
                                <FaMapMarkerAlt />
                                <span>{settings.address}</span>
                            </span>
                        </div>
                    </div>

                    {/* Quick Navigation Links */}
                    <div className="col-lg-2 col-md-6 col-6">
                        <h4 className="footer-col-title">Navigation</h4>
                        <div className="footer-links-list">
                            <Link to="/" className="footer-nav-link"><FaChevronRight className="me-1 small-icon" />Home</Link>
                            <Link to="/about" className="footer-nav-link"><FaChevronRight className="me-1 small-icon" />About Us</Link>
                            <Link to="/gallery" className="footer-nav-link"><FaChevronRight className="me-1 small-icon" />Gallery</Link>
                            <Link to="/construction" className="footer-nav-link"><FaChevronRight className="me-1 small-icon" />Construction</Link>
                            <Link to="/team" className="footer-nav-link"><FaChevronRight className="me-1 small-icon" />Our Team</Link>
                            <Link to="/contact" className="footer-nav-link"><FaChevronRight className="me-1 small-icon" />Contact</Link>
                        </div>
                    </div>

                    {/* How to Help */}
                    <div className="col-lg-2 col-md-6 col-6">
                        <h4 className="footer-col-title">Get Involved</h4>
                        <div className="footer-links-list">
                            <Link to="/donate" className="footer-nav-link"><FaChevronRight className="me-1 small-icon" />Donate Now</Link>
                            <Link to="/construction" className="footer-nav-link"><FaChevronRight className="me-1 small-icon" />Building Project</Link>
                            <Link to="/contact" className="footer-nav-link"><FaChevronRight className="me-1 small-icon" />Volunteer</Link>
                            <Link to="/about" className="footer-nav-link"><FaChevronRight className="me-1 small-icon" />Our Story</Link>
                        </div>
                    </div>

                    {/* Newsletter and Socials */}
                    <div className="col-lg-4 col-md-6">
                        <div className="footer-newsletter-card">
                            <h4 className="footer-col-title">Stay Connected</h4>
                            <p style={{ fontSize: '0.9rem', color: '#cbd5e1', marginBottom: '0.75rem' }}>
                                Join our community and receive updates on our children's progress.
                            </p>
                            <form onSubmit={onsubmitBtn} className="newsletter-input-group">
                                <input
                                    type="email"
                                    className="newsletter-input"
                                    placeholder="Enter your email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                                <button type="submit" className="newsletter-btn">
                                    <FaPaperPlane className="me-2" /> Subscribe
                                </button>
                                {subscribed && (
                                    <p className="text-success small mt-1 mb-0">✔ Thank you for supporting our mission!</p>
                                )}
                            </form>

                            <div className="footer-social-row">
                                <a className="footer-social-btn" href={settings.socialLinks?.facebook || "#"} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                                    <FaFacebookF />
                                </a>
                                <a className="footer-social-btn" href={settings.socialLinks?.instagram || "#"} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                                    <FaInstagram />
                                </a>
                                <a className="footer-social-btn" href={settings.socialLinks?.youtube || "#"} target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                                    <FaYoutube />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Copyright Bar */}
            <div className="footer-bottom-bar">
                <div className="container">
                    <div className="footer-bottom-inner">
                        <p className="mb-0">
                            &copy; {new Date().getFullYear()} <Link to="/">{settings.copyrightText || "Loving Arms Orphanage"}</Link>. All Rights Reserved.
                        </p>
                        <div className="footer-dev-credit d-flex align-items-center gap-1">
                            <span>Built with</span>
                            <FaHeart className="text-danger mx-1" style={{ fontSize: '0.85rem' }} />
                            <span>by</span>
                            <a
                                href="https://wa.me/919014882779?text=i%20have%20a%20website%20requirement"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="footer-dev-link fw-bold"
                            >
                                Harte Labs Pvt Ltd
                            </a>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <Link to="/admin/dashboard" className="footer-admin-link">
                                <FaLock className="me-1" /> Admin Portal
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
}